import { useEffect, useState } from 'react';
import { brandingAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Palette, Building2, Image, Type, Mail, Phone, Globe, MapPin, 
  Save, Upload, Eye, RefreshCw, Shield
} from 'lucide-react';

export const BrandingSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    company_name: 'UnifiCRM',
    company_tagline: 'Your Complete Business Solution',
    logo_url: null,
    favicon_url: null,
    login_title: 'Welcome Back',
    login_subtitle: 'Sign in to your account',
    login_background_color: '#f8fafc',
    login_card_style: 'default',
    primary_color: '#3b82f6',
    secondary_color: '#10b981',
    accent_color: '#8b5cf6',
    support_email: '',
    support_phone: '',
    website_url: '',
    address: '',
    show_powered_by: true,
    custom_css: ''
  });

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const res = await brandingAPI.get();
      setBranding(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await brandingAPI.update(branding);
      toast.success('Branding settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const res = await brandingAPI.uploadLogo(file, type);
      setBranding(prev => ({
        ...prev,
        [type === 'logo' ? 'logo_url' : 'favicon_url']: res.data.url
      }));
      toast.success(`${type === 'logo' ? 'Logo' : 'Favicon'} uploaded!`);
    } catch (error) {
      toast.error('Upload failed');
    }
  };

  const handleChange = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">
              Only administrators can access branding settings.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="branding-settings-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Company Branding</h1>
          <p className="text-muted-foreground mt-1">
            Customize the look and feel of your CRM
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open('/login', '_blank')}>
            <Eye className="h-4 w-4 mr-2" />
            Preview Login
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-branding-btn">
            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="identity" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="identity">
            <Building2 className="h-4 w-4 mr-2" />
            Identity
          </TabsTrigger>
          <TabsTrigger value="login">
            <Type className="h-4 w-4 mr-2" />
            Login Page
          </TabsTrigger>
          <TabsTrigger value="colors">
            <Palette className="h-4 w-4 mr-2" />
            Colors
          </TabsTrigger>
          <TabsTrigger value="contact">
            <Mail className="h-4 w-4 mr-2" />
            Contact Info
          </TabsTrigger>
        </TabsList>

        {/* Identity Tab */}
        <TabsContent value="identity" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Identity
              </CardTitle>
              <CardDescription>
                Basic information about your company
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={branding.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="Your Company Name"
                    data-testid="branding-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_tagline">Tagline</Label>
                  <Input
                    id="company_tagline"
                    value={branding.company_tagline || ''}
                    onChange={(e) => handleChange('company_tagline', e.target.value)}
                    placeholder="Your Company Tagline"
                  />
                </div>
              </div>

              {/* Logo Upload */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label>Company Logo</Label>
                  <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary transition-colors">
                    {branding.logo_url ? (
                      <div className="space-y-3">
                        <img 
                          src={branding.logo_url} 
                          alt="Company Logo" 
                          className="max-h-20 mx-auto object-contain"
                        />
                        <p className="text-sm text-muted-foreground">Current logo</p>
                      </div>
                    ) : (
                      <Image className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleLogoUpload(e, 'logo')}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Logo
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-muted-foreground mt-2">
                      Recommended: 200x50px, PNG or SVG
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Favicon</Label>
                  <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary transition-colors">
                    {branding.favicon_url ? (
                      <div className="space-y-3">
                        <img 
                          src={branding.favicon_url} 
                          alt="Favicon" 
                          className="h-8 w-8 mx-auto"
                        />
                        <p className="text-sm text-muted-foreground">Current favicon</p>
                      </div>
                    ) : (
                      <Image className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*,.ico"
                        className="hidden"
                        onChange={(e) => handleLogoUpload(e, 'favicon')}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Favicon
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-muted-foreground mt-2">
                      Recommended: 32x32px, ICO or PNG
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Login Page Tab */}
        <TabsContent value="login" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="h-5 w-5" />
                Login Page Customization
              </CardTitle>
              <CardDescription>
                Customize the text and appearance of your login page
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="login_title">Login Title</Label>
                  <Input
                    id="login_title"
                    value={branding.login_title}
                    onChange={(e) => handleChange('login_title', e.target.value)}
                    placeholder="Welcome Back"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login_subtitle">Login Subtitle</Label>
                  <Input
                    id="login_subtitle"
                    value={branding.login_subtitle}
                    onChange={(e) => handleChange('login_subtitle', e.target.value)}
                    placeholder="Sign in to your account"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="login_card_style">Login Card Style</Label>
                  <Select 
                    value={branding.login_card_style} 
                    onValueChange={(v) => handleChange('login_card_style', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default (Clean)</SelectItem>
                      <SelectItem value="glass">Glass (Frosted)</SelectItem>
                      <SelectItem value="minimal">Minimal (No shadow)</SelectItem>
                      <SelectItem value="gradient">Gradient Border</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login_background_color">Background Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={branding.login_background_color}
                      onChange={(e) => handleChange('login_background_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={branding.login_background_color}
                      onChange={(e) => handleChange('login_background_color', e.target.value)}
                      placeholder="#f8fafc"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label>Show "Powered by" Footer</Label>
                  <p className="text-sm text-muted-foreground">
                    Display "Powered by UnifiCRM" on the login page
                  </p>
                </div>
                <Switch
                  checked={branding.show_powered_by}
                  onCheckedChange={(v) => handleChange('show_powered_by', v)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Login Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="rounded-lg p-8 min-h-[300px] flex items-center justify-center"
                style={{ backgroundColor: branding.login_background_color }}
              >
                <div 
                  className={`bg-white p-8 rounded-xl w-full max-w-sm ${
                    branding.login_card_style === 'glass' ? 'backdrop-blur-lg bg-white/80 shadow-xl' :
                    branding.login_card_style === 'minimal' ? '' :
                    branding.login_card_style === 'gradient' ? 'border-2 border-transparent bg-gradient-to-r from-blue-500 to-purple-500 p-[2px]' :
                    'shadow-lg'
                  }`}
                >
                  <div className={branding.login_card_style === 'gradient' ? 'bg-white rounded-lg p-6' : ''}>
                    {branding.logo_url && (
                      <img src={branding.logo_url} alt="Logo" className="h-8 mx-auto mb-4" />
                    )}
                    <h2 className="text-xl font-bold text-center mb-1">{branding.login_title}</h2>
                    <p className="text-sm text-gray-500 text-center mb-4">{branding.login_subtitle}</p>
                    <div className="space-y-3">
                      <div className="h-10 bg-gray-100 rounded animate-pulse" />
                      <div className="h-10 bg-gray-100 rounded animate-pulse" />
                      <div 
                        className="h-10 rounded"
                        style={{ backgroundColor: branding.primary_color }}
                      />
                    </div>
                    {branding.show_powered_by && (
                      <p className="text-xs text-gray-400 text-center mt-4">
                        Powered by {branding.company_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Colors Tab */}
        <TabsContent value="colors" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Brand Colors
              </CardTitle>
              <CardDescription>
                Define your brand's color palette
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <Label>Primary Color</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="color"
                      value={branding.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="w-16 h-12 p-1 cursor-pointer"
                    />
                    <div>
                      <Input
                        value={branding.primary_color}
                        onChange={(e) => handleChange('primary_color', e.target.value)}
                        className="w-28"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Buttons, links</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Secondary Color</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="color"
                      value={branding.secondary_color}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      className="w-16 h-12 p-1 cursor-pointer"
                    />
                    <div>
                      <Input
                        value={branding.secondary_color}
                        onChange={(e) => handleChange('secondary_color', e.target.value)}
                        className="w-28"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Success states</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Accent Color</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="color"
                      value={branding.accent_color}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                      className="w-16 h-12 p-1 cursor-pointer"
                    />
                    <div>
                      <Input
                        value={branding.accent_color}
                        onChange={(e) => handleChange('accent_color', e.target.value)}
                        className="w-28"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Highlights</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Color Preview */}
              <div className="p-4 border rounded-lg">
                <Label className="mb-3 block">Color Preview</Label>
                <div className="flex gap-3 flex-wrap">
                  <button 
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: branding.primary_color }}
                  >
                    Primary Button
                  </button>
                  <button 
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: branding.secondary_color }}
                  >
                    Secondary Button
                  </button>
                  <button 
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: branding.accent_color }}
                  >
                    Accent Button
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Info Tab */}
        <TabsContent value="contact" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Company Contact Information
              </CardTitle>
              <CardDescription>
                Contact details shown in the footer and support pages
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="support_email">Support Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="support_email"
                      type="email"
                      value={branding.support_email || ''}
                      onChange={(e) => handleChange('support_email', e.target.value)}
                      placeholder="support@company.com"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="support_phone">Support Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="support_phone"
                      value={branding.support_phone || ''}
                      onChange={(e) => handleChange('support_phone', e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="website_url">Website URL</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="website_url"
                      type="url"
                      value={branding.website_url || ''}
                      onChange={(e) => handleChange('website_url', e.target.value)}
                      placeholder="https://www.company.com"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Business Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="address"
                      value={branding.address || ''}
                      onChange={(e) => handleChange('address', e.target.value)}
                      placeholder="123 Main St, City, State"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BrandingSettingsPage;
